import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import { toast } from "react-toastify";
import PaletteOutlinedIcon from "@material-ui/icons/PaletteOutlined";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import ChatAppearanceEditor from "../../components/ChatAppearanceEditor";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import {
	ChatbotIcon,
	DepartmentsIcon,
	IntegrationsIcon,
	QueuesIcon,
} from "../../components/CrmIcons";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(8, 8, 3),
		[theme.breakpoints.down("xs")]: {
			padding: theme.spacing(3, 2),
		},
	},

	paper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		marginBottom: 12,
		[theme.breakpoints.down("xs")]: {
			alignItems: "flex-start",
			flexWrap: "wrap",
		},
	},

	settingOption: {
		marginLeft: "auto",
	},
	margin: {
		margin: theme.spacing(1),
	},
	settingText: {
		flex: 1,
		marginLeft: theme.spacing(1),
	},
	moduleButton: {
		marginLeft: theme.spacing(2),
		[theme.breakpoints.down("xs")]: {
			width: "100%",
			marginTop: theme.spacing(1.5),
			marginLeft: 0,
		},
	},

}));

const Settings = () => {
	const classes = useStyles();
	const history = useHistory();

	const [settings, setSettings] = useState([]);
	const [chatStyleOpen, setChatStyleOpen] = useState(false);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		socket.on("settings", data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					if (settingIndex === -1) aux.push(data.setting);
					else aux[settingIndex].value = data.setting.value;
					return aux;
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	const handleChangeSetting = async e => {
		const selectedValue = e.target.value;
		const settingKey = e.target.name;

		try {
			await api.put(`/settings/${settingKey}`, {
				value: selectedValue,
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const getSettingValue = key => {
		return settings.find(s => s.key === key)?.value || "";
	};

	const openThemePicker = () => {
		window.dispatchEvent(new Event("crm:open-theme-picker"));
	};

	return (
		<div className={classes.root}>
			<Container className={classes.container} maxWidth="sm">
				<Typography variant="body2" gutterBottom>
					{i18n.t("settings.title")}
				</Typography>
				<Paper className={classes.paper}>
					<PaletteOutlinedIcon color="primary" />
					<div className={classes.settingText}>
						<Typography variant="body1">Tema de color</Typography>
						<Typography variant="caption" color="textSecondary">
							Personalizá los colores y el modo visual de toda la interfaz.
						</Typography>
					</div>
					<Button className={classes.moduleButton} color="primary" variant="outlined" onClick={openThemePicker}>
						Elegir tema
					</Button>
				</Paper>
				<Paper className={classes.paper}>
					<ChatBubbleOutlineIcon color="primary" />
					<div className={classes.settingText}>
						<Typography variant="body1">Estilo del chat</Typography>
						<Typography variant="caption" color="textSecondary">Elegí los colores de las burbujas y el fondo, o usá una foto.</Typography>
					</div>
					<Button className={classes.moduleButton} color="primary" variant="outlined" onClick={() => setChatStyleOpen(true)}>Personalizar</Button>
				</Paper>
				<Paper className={classes.paper}>
					<QueuesIcon color="primary" />
					<div className={classes.settingText}>
						<Typography variant="body1">Colas</Typography>
						<Typography variant="caption" color="textSecondary">
							Organizá la atención y distribución de conversaciones.
						</Typography>
					</div>
					<Button className={classes.moduleButton} color="primary" variant="contained" onClick={() => history.push("/queues")}>
						Configurar
					</Button>
				</Paper>
				<Paper className={classes.paper}>
					<DepartmentsIcon color="primary" />
					<div className={classes.settingText}>
						<Typography variant="body1">Departamentos y bots</Typography>
						<Typography variant="caption" color="textSecondary">
							Administrá equipos, responsables y automatizaciones por área.
						</Typography>
					</div>
					<Button className={classes.moduleButton} color="primary" variant="contained" onClick={() => history.push("/departments")}>
						Configurar
					</Button>
				</Paper>
				<Paper className={classes.paper}>
					<ChatbotIcon color="primary" />
					<div className={classes.settingText}>
						<Typography variant="body1">Chatbot</Typography>
						<Typography variant="caption" color="textSecondary">
							Configurá respuestas automáticas y flujos de atención.
						</Typography>
					</div>
					<Button className={classes.moduleButton} color="primary" variant="contained" onClick={() => history.push("/chatbot")}>
						Configurar
					</Button>
				</Paper>
				<Paper className={classes.paper}>
					<IntegrationsIcon color="primary" />
					<div className={classes.settingText}>
						<Typography variant="body1">Aplicaciones e integraciones</Typography>
						<Typography variant="caption" color="textSecondary">
							Vinculá y administrá WhatsApp, Facebook, Instagram, Web y TikTok.
						</Typography>
					</div>
					<Button className={classes.moduleButton} color="primary" variant="contained" onClick={() => history.push("/meta")}>
						Configurar
					</Button>
				</Paper>
				<Paper className={classes.paper}>
					<Typography variant="body1">
						{i18n.t("settings.settings.userCreation.name")}
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="userCreation-setting"
						name="userCreation"
						value={
							settings && settings.length > 0 && getSettingValue("userCreation")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							{i18n.t("settings.settings.userCreation.options.enabled")}
						</option>
						<option value="disabled">
							{i18n.t("settings.settings.userCreation.options.disabled")}
						</option>
					</Select>

				</Paper>

				<Paper className={classes.paper}>
					<TextField
						id="api-token-setting"
						readonly
						label="Token Api"
						margin="dense"
						variant="outlined"
						fullWidth
						value={settings && settings.length > 0 && getSettingValue("userApiToken")}
					/>
				</Paper>

				<ChatAppearanceEditor open={chatStyleOpen} onClose={() => setChatStyleOpen(false)} />
			</Container>
		</div>
	);
};

export default Settings;
